import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import GroupedColorClarityGrid from "./groupedColorClarityGrid";
import { getBgColor, mergeFilters } from "../utils/utils";
import { useGetColorClarityDistributionQuery, useGetHigherLevelAnalysisColorClarityQuery } from "../redux/slices/apiSlice";
import { useDispatch, useSelector } from "react-redux";
import { pricing, setIsPreFiltered, setPreDefinedFilter } from "../redux/slices/pricingSlice";
import { useLocation, useNavigate } from "react-router-dom";
import DetailedColorClarity from "./DetailedColorClarity";
import StockAndSales from "./StockAndSales";
import { Loader } from "lucide-react/dist/cjs/lucide-react";
import { CircularProgress } from "@mui/material";
import { ColorRing } from "react-loader-spinner";
import {
  setSelectedCell,
  setSelectedGroupedCell,
  setSelectedColors,
  setSelectedClarities,
  setSelectedColorGroup,
  setSelectedClarityGroup,
} from "../redux/slices/colorClaritySlice"
import HigherLevelData from "./HigherLevelAnalysis/HigherLevelData";
import { FaMinus, FaPlus} from "react-icons/fa";
import { skipToken } from "@reduxjs/toolkit/query";
import MetricsPagesCardMUI from "./metricSummaryUI";

const ColorClarityGrid = ({
  isCsr, isAdmin,isMFG, queryParams, shape,isResetting, caratRange, selectedMetric, selectedNeedsMetric, highlightedGroup, onCellClick, setHoveredGroup,data, gridData,shapes,  caratRanges, selectedShape,setSelectedShape,selectedCaratRange,setSelectedCaratRange, handleShapeChange,handleCaratChange, idealTurn, formatTooltipPriceM, idealInterest,roundToZeroDecimal}) => {
  
  const colors = ["0000d","0001e","0002f", "0003g","0004h","0005i", "0006j","0007k", "0008l","0009m","0010n","0011o","0012p","0013q","0014r","0015s","0016t","0017u","0018v", "0019w", "0020x","0021y","0022z"];
  const clarities = ["0000fl","0001if","0002vvs1","0003vvs2","0004vs1","0005vs2", "0006si1", "0007si2", "0008si3","0009i1","0010p1","0011i2","0012p2","0013i3","0014p3" ];

  // State for data
  const [hideHigherlevel, setHideHigherLevel]= useState(false);
  const ready = !!queryParams.shape && !!queryParams.carat_range;
  const readyHigh = !!queryParams.shape && !!queryParams.carat_range && hideHigherlevel;
  const [isDataLoading, setIsDataLoading] = useState(true);
  const { data: newData, isLoading: isColorClarityLoading } = useGetColorClarityDistributionQuery(ready ? queryParams : skipToken,  {
    refetchOnMountOrArgChange: true
  });

  const { 
    data: higherLevelData, 
    isLoading: isHigherLevelLoading, 
    isFetching:isHigherLevelFetching, refetch  
    } = useGetHigherLevelAnalysisColorClarityQuery(
      readyHigh ? queryParams : skipToken, {
    refetchOnMountOrArgChange: true
  });
  const [colorKeys, setColorKeys] = useState([]);
  const [colorGroupKeys, setColorGroupKeys] = useState([]);
  const [clarityDatas, setClarityDatas] = useState([]);
  const [allClarityRanges, setAllClarityRanges] = useState([]);
  const [colorGroupsApi, setColorGroupsApi] = useState({});
  const [clarityGroupsApi, setClarityGroupsApi] = useState({});
  const [salesFilterKey, setSalesFilterKey] = useState(0);
  const [isChangingParams, setIsChangingParams] = useState(false);
  const drilldownRef = useRef(null);
  const [inventoryFilterKey, setInventoryFilterKey] = useState(0);
  const [summaryMetaData, setSummaryMetaData] = useState({});
  const [higherLevelColorKeys, setHigherLevelColorKeys] = useState([]);
  const [higherLevelClarityDatas, setHigherLevelClarityDatas] = useState([]);
  const [allHigherLevelClarityRanges, setAllHigherLevelClarityRanges] = useState([]);


  // const [selectionMode, setSelectionMode] = useState('individual');
  const dispatch = useDispatch();  
  const navigate = useNavigate();
  const location = useLocation();
  const {
    selectedColors,
    selectedClarities,
    selectedCell,
    selectedColorGroup,
    selectedClarityGroup,
    selectedGroupedCell,
    selectionMode
  } = useSelector((state) => state.colorClarity); 
  
  const transformJsonToFiltered = (jsonFiltered) => {
      const result = {
        discrete: { ...jsonFiltered?.item_filter.discrete }  // Copy existing discrete values
      };
      
      // Convert from/to pairs into discrete array entries
      const { from, to } = jsonFiltered.item_filter;
      
      // Process all from/to pairs
      for (const key in from) {
        if (to[key]) {  // Only process if both from and to exist for the key
          result.discrete[key] = [{
            from: String(from[key]),  // Convert to string to ensure consistent format
            to: String(to[key])
          }];
        }
      }
      
      return result;
    };
  let tableFiltered = transformJsonToFiltered(queryParams.filters);
  const [salesFilter, setSalesFilter] = useState({
    isCsr: true,
    filterHash: {
      size: 1,
      end_date: queryParams.endDate, // Current date
      start_date: queryParams.startDate, // First day of current year
      show_mine: true,
      show_others: true,
      is_sale_report: true,
      seller_account_id: [],
      get_filtered_data: true,
      item_filter: {
        discrete: {
          "diamond#clarity": [selectedCell?.clarity].filter(Boolean),
          "diamond#color": [selectedCell?.color].filter(Boolean),
          "diamond#shape": [selectedShape],
          "gem#carats": [
          {
            "from": selectedCaratRange.split('-')[0],
            "to": selectedCaratRange.split('-')[1]
          }
          ], ...tableFiltered?.discrete,
        }
      },
      get_only_matching_count: true,
      change_breakdown: true
    },
    rowFilter: {}
  });

  const [inventoryFilter, setInventoryFilter] = useState({
    isCsr: true,
    filterHash: {
      size: 1,
      end_date: new Date().toISOString().split('T')[0], // Current date
      start_date: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0], // First day of current year
      show_mine: true,
      show_others: true,
      is_sale_report: false,
      seller_account_id: [],
      get_filtered_data: true,
      item_filter: {
        discrete: {
        "diamond#clarity": [selectedCell?.clarity].filter(Boolean),
        "diamond#color": [selectedCell?.color].filter(Boolean),
        "diamond#shape": [selectedShape],
        "gem#carats": [
        {
          "from": selectedCaratRange.split('-')[0],
          "to": selectedCaratRange.split('-')[1]
        }
        ],
        ...tableFiltered?.discrete,
        }
      },
      get_only_matching_count: true,
      change_breakdown: true
    },
    rowFilter: {}
  });

  const getCellStyle = (bgColor, color, clarity, cellData, outline) => {
    const isSelected = selectedCell?.color === color && selectedCell?.clarity === clarity;
    const highlighted = isHighlighted(color, clarity);
    const boxShadow = highlighted ? getBoxShadow(colors.indexOf(color), clarities.indexOf(clarity)) : "none";
    
      return {
        backgroundColor: bgColor,
        boxShadow: highlighted ? boxShadow : "none",
        fontWeight: highlighted ? "bold" : "normal",
        border: isSelected ? "2px solid #000" : "1px solid #ccc",
        outline: outline,
        // transition: "all 0.2s ease",
      };
  };
  
  useEffect(() => {
    if (isResetting) {
      const newFilter = {
        discrete: {
          "diamond#clarity": (selectionMode !=="grouped" ? selectedClarities: selectedClarityGroup).filter(Boolean),
          "diamond#color": (selectionMode !=="grouped" ? selectedColors: selectedColorGroup).filter(Boolean),
          "diamond#shape": [selectedShape],
          "gem#carats": [{
            "from": selectedCaratRange.split('-')[0],
            "to": selectedCaratRange.split('-')[1]
          }],
        }
      };
  
      setSalesFilter(prev => ({
        ...prev,
        filterHash: {
          ...prev.filterHash,
          item_filter: newFilter
        }
      }));
  
      setInventoryFilter(prev => ({
        ...prev,
        filterHash: {
          ...prev.filterHash,
          item_filter: newFilter
        }
      }));
      setSalesFilterKey(prev => prev + 1);
      setInventoryFilterKey(prev => prev + 1);
    }

    else if(!isResetting){
      const newFilter = {
        discrete: {
          "diamond#clarity": (selectionMode !=="grouped" ? selectedClarities: selectedClarityGroup).filter(Boolean),
          "diamond#color": (selectionMode !=="grouped" ? selectedColors: selectedColorGroup).filter(Boolean),
          "diamond#shape": [selectedShape],
          "gem#carats": [{
            "from": selectedCaratRange.split('-')[0],
            "to": selectedCaratRange.split('-')[1]
          }],
          ...transformJsonToFiltered(queryParams.filters)?.discrete,
        }
      };

  
      setSalesFilter(prev => ({
        ...prev,
        filterHash: {
          ...prev.filterHash,
          item_filter: newFilter
        }
      }));
  
      setInventoryFilter(prev => ({
        ...prev,
        filterHash: {
          ...prev.filterHash,
          item_filter: newFilter
        }
      }));
      setSalesFilterKey(prev => prev + 1);
      setInventoryFilterKey(prev => prev + 1);
    }
    
  }, [isResetting, queryParams.filters]);

  // write a function to return number with one decimal place for any given number or integer
  function roundToOneDecimal(value) {
    return (Math.round(value * 10) / 10).toFixed(1);
  }
  
  function roundIfFloat(value) {
    // Check if the value is a number and if it has a decimal part
    if (typeof value === 'number' && value % 1 !== 0) {
      // Round the number to 2 decimal places
      return Math.round(value * 100) / 100;
    }
    // Return the value as it is if it's not a float
    return value;
  }
  
  // Define matching logic for highlighted cells
  const isHighlighted = (color, clarity) => {
    if (!highlightedGroup) return false;

    const colorGroup = colorGroupsApi[highlightedGroup[0]];
    const clarityGroup = clarityGroupsApi[highlightedGroup[1]];

    return (
      (colorGroup && colorGroup.includes(color)) &&
      (clarityGroup && clarityGroup.includes(clarity))
    );
  };

  // Check if a cell is on the boundary of a group
  const getBoxShadow = (rowIndex, colIndex) => {
    if (!isHighlighted(colors[rowIndex], clarities[colIndex])) return "";

    const isTopEdge =
      rowIndex === 0 ||
      !isHighlighted(colors[rowIndex - 1], clarities[colIndex]);
    const isBottomEdge =
      rowIndex === colors.length - 1 ||
      !isHighlighted(colors[rowIndex + 1], clarities[colIndex]);
    const isLeftEdge =
      colIndex === 0 ||
      !isHighlighted(colors[rowIndex], clarities[colIndex - 1]);
    const isRightEdge =
      colIndex === clarities.length - 1 ||
      !isHighlighted(colors[rowIndex], clarities[colIndex + 1]);

    const shadows = [];
    if (isTopEdge) shadows.push("0px -4px 4px rgba(0, 0, 0, 0.5)");
    if (isBottomEdge) shadows.push("0px 4px 4px rgba(0, 0, 0, 0.5)");
    if (isLeftEdge) shadows.push("-4px 0px 4px rgba(0, 0, 0, 0.5)");
    if (isRightEdge) shadows.push("4px 0px 4px rgba(0, 0, 0, 0.5)");

    return shadows.join(", ");
  };

  const handleCellClick = (color, clarity, inventory, sales, cellData) => {
    // Set selection mode and update cell selection
    // dispatch(setSelectionMode('individual'));
    // // setSelectedCell({ color, clarity, inventory, sales })
    // setSelectedColors([color]);
    // setSelectedClarities([clarity]);
    // setSelectedCell({
    //   inventory,
    //   sales,
    //   color: color,
    //   clarity: clarity
    // });
    handleSelectionChange({ colors: [color], clarities: [clarity] });
    // setSelectedCell({ color, clarity, inventory, sales });
    dispatch(setSelectedCell({
      inventory,
      sales,
      color: color,
      clarity: clarity
    }));
    onCellClick(color, clarity, cellData);
    const url = new URL(window.location);
    
      url.searchParams.set("sh", selectedShape);
      url.searchParams.set("cr", selectedCaratRange);
      url.searchParams.set("col", color);
      url.searchParams.set("clr", clarity);
      window.history.replaceState({}, "", url);
    
    // Clear grouped selections
    // setSelectedGroupedCell(null);
    // setSelectedColorGroup([]);
    // setSelectedClarityGroup([]);
  
  
    // Smooth scroll to the drilldown section
    setTimeout(() => {
      drilldownRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 100);
  };

  const handleHigherLevelCellClick = (color, clarity, inventory, sales, cellData) => {
    
    handleSelectionChange({ colors: [color], clarities: [clarity] });
    // setSelectedCell({ color, clarity, inventory, sales });
    
    dispatch(setSelectedCell({
      inventory,
      sales,
      color: color,
      clarity: clarity
    }));
    // Smooth scroll to the drilldown section
    setTimeout(() => {
      drilldownRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 100);
  };
  
  const transformFilters = (filters) => {
    const transformed = {
      item_filter: {
        discrete: {},
        from: {},
        text: {},
        to: {},
      },
    };
  
    // Process discrete filters
    if (filters.discrete) {
      for (const key in filters.discrete) {
        if (filters.discrete.hasOwnProperty(key)) {
          // Exclude gem#carats for now
          if (key !== "gem#carats" && key !== "diamond#make") {
            transformed.item_filter.discrete[key] = filters.discrete[key];
          } else if (key === "gem#carats") {
            // Combine gem#carats ranges into from and to
            const carats = filters.discrete["gem#carats"];
            let min = null;
            let max = null;
            carats.forEach((carat) => {
              if (carat.from !== undefined) {
                min = min !== null ? Math.min(min, parseFloat(carat.from)) : parseFloat(carat.from);
              }
              if (carat.to !== undefined) {
                max = max !== null ? Math.max(max, parseFloat(carat.to)) : parseFloat(carat.to);
              }
            });
            if (min !== null) transformed.item_filter.from["gem#carats"] = min.toString();
            if (max !== null) transformed.item_filter.to["gem#carats"] = max.toString();
          }
        }
      }
    }
  
    // Process rowFilters for carats (assuming it contains gem#carats)
      
    for (const key in transformed.item_filter.discrete) {
      if (Array.isArray(transformed.item_filter.discrete[key])) {
        transformed.item_filter.discrete[key] = [...new Set(transformed.item_filter.discrete[key])];
      }
    }
  
    return transformed;
  };


  const handleGroupedCellClick = ({ colorGroup, clarityGroup }) => {
    
    // dispatch(setSelectedGroupedCell({ 
    //   colorGroup: colorGroupsApi[colorGroup], 
    //   clarityGroup: clarityGroupsApi[clarityGroup] 
    // }));

    dispatch(setSelectedGroupedCell({ 
      colorGroup, 
      clarityGroup 
    }));
    
    // Dispatch color and clarity selections
    dispatch(setSelectedColorGroup(colorGroupsApi[colorGroup] || []));
    dispatch(setSelectedClarityGroup(clarityGroupsApi[clarityGroup] || []));
    handleSelectionChange({ colorGroup, clarityGroup });
    // Call the original click handler
    onCellClick(
      colorGroupsApi[colorGroup]?.[0] || '', 
      clarityGroupsApi[clarityGroup]?.[0] || '', 
      {} // You might want to pass appropriate cell data here
    );
    const colorCodes = colorGroupsApi[colorGroup] || [];
    const clarityCodes = clarityGroupsApi[clarityGroup] || [];

    const url = new URL(window.location);
    url.searchParams.set("sh", selectedShape);
    url.searchParams.set("cr", selectedCaratRange);
    url.searchParams.set("col", colorCodes.join(","));
    url.searchParams.set("clr", clarityCodes.join(","));
    window.history.replaceState({}, "", url);

  };


      
  const handleSelectionChange = ({ colors, clarities }) => {
    // colors and clarities will be arrays of individual values
    // whether selected individually or as part of a group
    dispatch(setSelectedColors(colors))
    dispatch(setSelectedClarities(clarities));
    // selectedClarityGroup(clarities)
    //  selectedColorGroup(colors)
  };
     
  const handleSearchClick = (value) => {
    if (value.item_filter.discrete["diamond#shape"]?.includes("0007cushion_brilliant")) {
      value.item_filter.discrete["diamond#shape"].push("0008cushion_modified");
    }
    const discreteFilters = mergeFilters(queryParams.filters,transformFilters(value.item_filter));
    dispatch(setIsPreFiltered(true));
    dispatch(setPreDefinedFilter(discreteFilters));
    navigate(`/price_co-pilot?page=0`);
  };

  const LoadingSkeleton = () => (
     <div className="flex justify-center items-center h-20 space-x-2">
        <ColorRing
          visible={true}
          height="60"
          width="60"
          ariaLabel="blocks-loading"
          wrapperStyle={{}}
          wrapperClass="blocks-wrapper"
          colors={["#00319f", "#2e56b1", "#5c7bc2", "#8aa1d3", "#b2c1e2"]}
        />
      </div>
     );
     useEffect(() => {
        const params = new URLSearchParams(location.search);
        const shapeParam = params.get("sh");
        const caratParam = params.get("cr");
        const colorParam = params.get("col");
        const clarityParam = params.get("clr");

        // Only set if all are present
        if (shapeParam && caratParam && colorParam && clarityParam) {
          // If color/clarity are comma-separated, use arrays
          const colorArr = colorParam.split(",");
          const clarityArr = clarityParam.split(",");

          // If single value, treat as individual cell
          if (colorArr.length === 1 && clarityArr.length === 1) {
            dispatch(setSelectedColors(colorArr));
            dispatch(setSelectedClarities(clarityArr));
            dispatch(setSelectedCell({
              color: colorArr[0],
              clarity: clarityArr[0],
              // Optionally: inventory/sales/cellData if you can look up from your data
            }));
          } else {
            // Grouped selection
            dispatch(setSelectedColorGroup(colorArr));
            dispatch(setSelectedClarityGroup(clarityArr));
            dispatch(setSelectedCell({
              color: colorArr,
              clarity: clarityArr,
            }));
          }
          // Optionally set shape/carat range if you want
          // setSelectedShape(shapeParam);
          // setSelectedCaratRange(caratParam);
        }
      // Only run on mount or when location.search changes
  }, [location.search]);
    useEffect(() => {
    if (selectedCell) {
      const newFilter = {
        discrete: {
          "diamond#clarity": [selectedCell.clarity].filter(Boolean),
          "diamond#color": [selectedCell.color].filter(Boolean),
          "diamond#shape": [selectedShape],
          "gem#carats": [{
            "from": selectedCaratRange.split('-')[0],
            "to": selectedCaratRange.split('-')[1]
          }],
          ...tableFiltered?.discrete,
        }
      };
  
      setSalesFilter(prev => ({
        ...prev,
        filterHash: {
          ...prev.filterHash,
          item_filter: newFilter
        }
      }));
  
      setInventoryFilter(prev => ({
        ...prev,
        filterHash: {
          ...prev.filterHash,
          item_filter: newFilter
        }
      }));
  
      setSalesFilterKey(prev => prev + 1);
      setInventoryFilterKey(prev => prev + 1);
    }
  }, [selectedCell, selectedShape, selectedCaratRange]);

  useEffect(() => {
    setSalesFilter(prevFilter => ({
      ...prevFilter,
      filterHash: {
        ...prevFilter.filterHash,
        end_date: queryParams.endDate, // Current date
        start_date: queryParams.startDate, // First day of current year
      }
    }));
  }, [queryParams]);
  // UseEffect for Shape changes
  useEffect(() => {
    setSalesFilter(prevFilter => ({
      ...prevFilter,
      filterHash: {
        ...prevFilter.filterHash,
        item_filter: {
          discrete: {
            ...prevFilter.filterHash.item_filter.discrete,
            "diamond#shape": [selectedShape]
          }
        }
      }
    }));

    setInventoryFilter(prevFilter => ({
      ...prevFilter,
      filterHash: {
        ...prevFilter.filterHash,
        item_filter: {
          discrete: {
            ...prevFilter.filterHash.item_filter.discrete,
            "diamond#shape": [selectedShape]
          }
        }
      }
    }));
    setSalesFilterKey(prev => prev + 1);
    setInventoryFilterKey(prev => prev + 1);
    setIsDataLoading(true);
  }, [selectedShape]);

  // UseEffect for Carat Range changes
  useEffect(() => {
    setSalesFilter(prevFilter => ({
      ...prevFilter,
      filterHash: {
        ...prevFilter.filterHash,
        item_filter: {
          discrete: {
            ...prevFilter.filterHash.item_filter.discrete,
            "gem#carats": [
              {
                "from": selectedCaratRange.split('-')[0],
                "to": selectedCaratRange.split('-')[1]
              }
            ]
          }
        }
      }
    }));
    setInventoryFilter(prevFilter => ({
      ...prevFilter,
      filterHash: {
        ...prevFilter.filterHash,
        item_filter: {
          discrete: {
            ...prevFilter.filterHash.item_filter.discrete,
            "gem#carats": [
              {
                "from": selectedCaratRange.split('-')[0],
                "to": selectedCaratRange.split('-')[1]
              }
            ]
          }
        }
      }
    }));
    setSalesFilterKey(prev => prev + 1);
    setInventoryFilterKey(prev => prev + 1)
    setIsDataLoading(true);
  }, [selectedCaratRange]);

  // UseEffect for Color/Clarity changes
  // UseEffect for Color changes
  useEffect(() => {
    setSalesFilter((prevFilter) => ({
      ...prevFilter,
      filterHash: {
        ...prevFilter.filterHash,
        item_filter: {
          discrete: {
            ...prevFilter.filterHash.item_filter.discrete,
            "diamond#color": (selectedColors && selectedColors.length ? selectedColors : [selectedCell?.color]).filter(Boolean),
          },
        },
      },
    }));

    setInventoryFilter((prevFilter) => ({
      ...prevFilter,
      filterHash: {
        ...prevFilter.filterHash,
        item_filter: {
          discrete: {
            ...prevFilter.filterHash.item_filter.discrete,
            "diamond#color": (selectedColors && selectedColors.length ? selectedColors : [selectedCell?.color]).filter(Boolean),
          },
        },
      },
    }));

    setSalesFilterKey(prev => prev + 1);
    setInventoryFilterKey(prev => prev + 1);
  }, [selectedColors, selectedCell?.color]);

  // UseEffect for Clarity changes
  useEffect(() => {
    setSalesFilter((prevFilter) => ({
      ...prevFilter,
      filterHash: {
        ...prevFilter.filterHash,
        item_filter: {
          discrete: {
            ...prevFilter.filterHash.item_filter.discrete,
            "diamond#clarity": (selectedClarities && selectedClarities.length ? selectedClarities : [selectedCell?.clarity]).filter(Boolean),
          },
        },
      },
    }));

    setInventoryFilter((prevFilter) => ({
      ...prevFilter,
      filterHash: {
        ...prevFilter.filterHash,
        item_filter: {
          discrete: {
            ...prevFilter.filterHash.item_filter.discrete,
            "diamond#clarity": (selectedClarities && selectedClarities.length ? selectedClarities : [selectedCell?.clarity]).filter(Boolean),
          },
        },
      },
    }));

    setSalesFilterKey(prev => prev + 1);
    setInventoryFilterKey(prev => prev + 1);
  }, [selectedClarities, selectedCell?.clarity]);
  
  
  const handleColorChange = (colors) => {
    if(colors.length > 1){
      dispatch(setSelectedColorGroup(colors));
      
    }
    else{
    dispatch(setSelectedColors(colors));
  }
  };

  const handleClarityChange = (clarities) => {
    if(clarities.length > 1){
      dispatch(setSelectedClarityGroup(clarities)); 
    }
    else{
      dispatch(setSelectedClarities(clarities));
    }

  };

  
  const [hoveredCell, setHoveredCell] = useState(null);
  const handleCellHover = (column, row) => {
      setHoveredCell(column !== null && row !== null ? { column, row } : null);
    };
  
 

  useEffect(() => {
    // console.log("isColorClarityLoading is ", isColorClarityLoading );
    if (!isColorClarityLoading){
      if (newData?.data) {
        const keys = Object.keys(newData.data);
        setColorKeys(keys);
        
        const mappedData = Object.values(newData.data).map(category =>
          Object.values(category)
        );
  
        const clarityfilterData = mappedData.map(category => 
          category.filter(item => typeof item === 'object')
        );
        setClarityDatas(clarityfilterData);
  
        Object.keys(newData.data).forEach(shapeKey => {
          // Extract carat ranges for this specific shape
          const clarityRanges = Object.keys(newData.data[shapeKey]).filter(key => key !== 'trans');
          // Add carat ranges to the array
          setAllClarityRanges(clarityRanges)
  
        });
      }
      if (newData?.grouped_data) {
  
        // const colorGroups = {};
        const colorGroups = {
          "D, E, F": ["0000d","0001e","0002f"],
          "G": ["0003g"],
          "H": ["0004h"],
          "I, J": ["0005i", "0006j"],
          "Others": ["0007k", "0008l","0009m","0010n","0011o","0012p","0013q","0014r","0015s","0016t","0017u","0018v", "0019w", "0020x","0021y","0022z"]
        };
        setColorGroupsApi(colorGroups)
        
        const clarityGroups = {
          "VVS+": ["0000fl","0001if","0002vvs1","0003vvs2"],
          "VS": ["0004vs1","0005vs2"],
          "SI1": ["0006si1"],
          "SI2": ["0007si2"],
          "I": [ "0008si3", "0009i1","0010p1","0011i2","0012p2","0013i3","0014p3"],
        };
        setClarityGroupsApi(clarityGroups);
      
      }
      if (newData?.meta) {
        setSummaryMetaData(newData.meta);
      }
    }
  }, [newData?.data]);
  
//  useEffect(() => {
//      if (!isHigherLevelLoading && higherLevelData?.data) {
//        const keys = Object.keys(higherLevelData.data);
//        setHigherLevelColorKeys(keys);
       
//        const mappedHigherLevelData = Object.values(higherLevelData.data).map(
//          category => Object.values(category)
//        );
//        const clarityRanges = [];
//        keys.forEach(shapeKey => {
//          const ranges = Object.keys(higherLevelData.data[shapeKey]).filter(
//            key => key !== 'trans'
//          );
//          clarityRanges.push(...ranges);
//        });
       
//        setAllHigherLevelClarityRanges([...new Set(clarityRanges)]);
 
//        const clarityfilterData = mappedHigherLevelData.map(
//          category => category.filter(item => typeof item === 'object')
//        );
       
//        setHigherLevelClarityDatas(clarityfilterData);
       
       
       
//        // setIsChangingGrading(false);
//      }
//    }, [selectedShape,selectedCaratRange]);

   useEffect(() => {
    if (!isHigherLevelLoading && !isHigherLevelFetching && higherLevelData?.data) {

      try {
        const keys = Object.keys(higherLevelData.data);
        setHigherLevelColorKeys(keys);
        
        const mappedHigherLevelData = Object.values(higherLevelData.data).map(
          category => Object.values(category)
        );
        
        const clarityRanges = [];
        keys.forEach(shapeKey => {
          const ranges = Object.keys(higherLevelData.data[shapeKey]).filter(
            key => key !== 'trans'
          );
          clarityRanges.push(...ranges);
        });
        
        setAllHigherLevelClarityRanges([...new Set(clarityRanges)]);
  
        const clarityfilterData = mappedHigherLevelData.map(
          category => category.filter(item => typeof item === 'object')
        );
        
        setHigherLevelClarityDatas(clarityfilterData);
      } catch (error) {
        console.error("Error processing higher level data:", error);
      }
    }
  }, [isHigherLevelLoading, isHigherLevelFetching, selectedShape, selectedCaratRange]);
     
  const isHigherLevelDataLoading = isHigherLevelLoading || isHigherLevelFetching;
  useEffect(() => {

    if (selectionMode === 'grouped') {
      const colorOptions = colorGroupsApi[selectedColorGroup] || [];
      setColorGroupKeys(colorOptions);
    
      setSalesFilter(prevFilter => ({
        ...prevFilter,
        filterHash: {
          ...prevFilter.filterHash,
          item_filter: {
            discrete: {
              ...prevFilter.filterHash.item_filter.discrete,
              "diamond#clarity": selectedClarityGroup,
              "diamond#color": selectedColorGroup,
            },
          },
        },
      }));

      setInventoryFilter(prevFilter => ({
        ...prevFilter,
        filterHash: {
          ...prevFilter.filterHash,
          item_filter: {
            discrete: {
              ...prevFilter.filterHash.item_filter.discrete,
              "diamond#clarity": selectedClarityGroup,
              "diamond#color": selectedColorGroup,
            },
          },
        },
      }));
    }

    setSalesFilterKey(prev => prev + 1);
    setInventoryFilterKey(prev => prev + 1);

    setTimeout(() => {
      drilldownRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 100);
  }, [selectedClarityGroup, selectedColorGroup, salesFilter.rowFilter]);

  
  useEffect(() => {
    if (!isColorClarityLoading && newData) {
      setIsDataLoading(false);
    }
  }, [isColorClarityLoading, newData]);
  
  function roundToTwoDecimal(value) {
          return (Math.round(value * 10) / 10).toFixed(2);
  }
  const formatTooltipPrice = (value) => {
      if (value == null || isNaN(value)) {
        return "0"; // Handle null, undefined, or NaN values
      }
      if (value >= 1_000_000_000) {
        return `$${(value / 1_000).toFixed(0)}K`;
      } else if (value >= 1_000_000) {
        return `$${(value / 1_000).toFixed(0)}K`;
      } else if (value >= 1_000) {
        return `$${(value / 1_000).toFixed(0)}K`;
      }
      return `$${value.toFixed(0)}`;
    };
  return (
    <>
    <div className='flex gap-2 justify-start  items-center mb-6 mx-auto w-[1040px] pl-6 '>
          
          <div className="flex pb-1 w-full justify-between items-center flex-wrap text-[0.75rem]">
           <div className="flex gap-2 justify-between items-center">
              {/* <p className="text-inherit text-base font-medium ">Summary:</p>
              {isAdmin ? (
                <span className=" text-[0.6959rem] pt-[0.2rem] font-medium">
                  Inventory: {summaryMetaData?.inventory} | Inv Value: {formatTooltipPriceM(summaryMetaData.inv_value)} |
                  Sales: {summaryMetaData?.sales} | Sold Value: {formatTooltipPriceM(summaryMetaData.sold_value)} | Turn: {roundIfFloat(summaryMetaData.turn)} | Overhang:
                  {roundToOneDecimal(summaryMetaData.overhang)} | Needs/Surplus: {roundToOneDecimal(summaryMetaData.needs)} | GMROI: {roundToGrossMargin(summaryMetaData.gross_margin_roi)}
                    | GM: {roundToGrossMargin(summaryMetaData.gross_margin)}
                </span>
              ):(
                <span className=" text-[0.6959rem] pt-[0.2rem] font-medium">
                  Inventory: {summaryMetaData?.inventory} | Inv Value: {formatTooltipPriceM(summaryMetaData.inv_value)} |
                  Sales: {summaryMetaData?.sales} | Sold Value: {formatTooltipPriceM(summaryMetaData.sold_value)} | Turn: {roundIfFloat(summaryMetaData.turn)} | Overhang:
                  {roundToOneDecimal(summaryMetaData.overhang)} | Needs/Surplus: {roundToOneDecimal(summaryMetaData.needs)}
                </span>
              )} */}
              
           </div>
             <div className="flex gap-[0.2rem] w-full justify-between items-center">          
               <MetricsPagesCardMUI metaSummary={summaryMetaData} isAdmin={isAdmin} roundToOneDecimal={roundToOneDecimal} roundToZeroDecimal={roundToZeroDecimal} formatTooltipPriceM={formatTooltipPriceM} />           
             </div>
          </div>
    </div>
    <div className="color-clarity-grid row w-[1198px] flex justify-center items-start mx-auto">
     
      {/* Grouped View */}
      <div className="col-sm-12 col-md-4">
        <h3 className="text-center text-inherit text-2xl font-medium ">Grouped</h3>
        {isDataLoading ? (
          <LoadingSkeleton />
        ) : (
          newData?.data && (
            <GroupedColorClarityGrid
              isAdmin={isAdmin}
              idealTurn={idealTurn}
              idealInterest={idealInterest}
              colorClarityData={newData?.data}
              color={colorKeys}
              clarity={allClarityRanges}
              onGroupedCellClick={handleGroupedCellClick}
              selectedMetric={selectedMetric}
              onHoverGroup={(color, clarity) => setHoveredGroup([color, clarity])}
              selectedGroupedCell={selectedGroupedCell}
              selectedNeedsMetric={selectedNeedsMetric}
            />
          )
        )}
        
      </div>

          {/* Detailed View */}
          {isDataLoading ? (
        <div className="col-sm-12 col-md-8">
        <h3 className="text-center text-inherit text-2xl font-medium ">Detailed</h3>
          <LoadingSkeleton />
        </div>
      ) : (
        <DetailedColorClarity
          isAdmin={isAdmin}
          idealTurn={idealTurn}
          isColorClarityLoading={isColorClarityLoading}
          newData={newData}
          shapeData={data}
          colorKeys={colorKeys}
          clarityDatas={clarityDatas}
          allClarityRanges={allClarityRanges}
          selectedMetric={selectedMetric}
          handleCellClick={handleCellClick}
          roundIfFloat={roundIfFloat}
          roundToOneDecimal={roundToOneDecimal}
          getBgColor={getBgColor}
          getCellStyle={getCellStyle}
          selectedCell={selectedCell}
          drilldownRef={drilldownRef}
          filtered={queryParams.filters}
          shape={shape}
          caratRange={caratRange}
          selectedNeedsMetric={selectedNeedsMetric}
          hoveredCell={hoveredCell}
          handleCellHover={handleCellHover}
        />
      )}
      </div>

      <br className="mt-10"></br>
      {/* <br></br> */}
      {/* <hr /> */}
      
      {/* Drilldown: Inventory and Sales Table */}
      {hideHigherlevel && (
        <div className={`transition-opacity duration-300 ${isHigherLevelDataLoading ? 'opacity-50' : 'opacity-100'}`}>
          {isHigherLevelDataLoading ? (
            <div className="flex justify-center items-center w-full h-40">
              <ColorRing
                visible={true}
                height="60"
                width="60"
                ariaLabel="blocks-loading"
                colors={["#00319f", "#2e56b1", "#5c7bc2", "#8aa1d3", "#b2c1e2"]}
              />
            </div>
          ) : (
            higherLevelData?.data && (
              <div className=" w-[85vw] mx-auto flex justify-center items-center mt-6">
                <HigherLevelData
                  isCsr={isCsr}
                  id="higherlevel"
                  showLoading={isHigherLevelDataLoading}
                  getCellStyle={getCellStyle}
                  higherLevelData={higherLevelData}
                  higherLevelClarityDatas={higherLevelClarityDatas}
                  higherLevelColorKeys={higherLevelColorKeys}
                  allHigherLevelClarityRanges={allHigherLevelClarityRanges}
                  defaultTurn={data?.ideal_turn}
                  isHigherLevelLoading={isHigherLevelDataLoading}
                  isChangingGrading={isChangingParams}
                  hoveredCell={hoveredCell}
                  handleCellHover={handleCellHover}
                  handleCellClick={handleHigherLevelCellClick}
                />
              </div>
            )
          )}
        </div>
      )}
      <div className="mt-4 flex justify-end min-w-[80vw] max-w-[95vw]">
      {hideHigherlevel ? (
        <div
          onClick={() => setHideHigherLevel(false)}
          className="flex w-max items-center gap-2 px-3 py-1   text-blue-600  hover:bg-gray-100 transition duration-200 cursor-pointer"
        >
          <FaMinus className="h-3 w-3" />
          Show Less
        </div>
      ) : (
        <div
          onClick={() => setHideHigherLevel(true)}
          className="flex w-max items-center gap-2 px-3 py-1  text-blue-600   hover:bg-gray-100 transition duration-200 cursor-pointer"
        >
          <FaPlus className="h-3 w-3" />
          Show More
        </div>
      )}
      </div>
      <br></br>
      <hr />

      
    <div className="color-clarity-grid row min-w-[80vw] max-w-[95vw] flex justify-center items-start mx-auto">
      {(
      // Show if selectedCell has color & clarity (individual cell)
      (selectedCell && selectedCell.color && selectedCell.clarity)
      // Or if selectedGroupedCell exists (grouped selection)
      || selectedGroupedCell
       )  && (
        <StockAndSales
          drilldownRef={drilldownRef}
          isAdmin={isAdmin}
          isMFG={isMFG}
          selectedCell={selectedCell}
          selectedGroupedCell={selectedGroupedCell}
          isCsr={isCsr}
          shape={selectedShape}
          caratRange={selectedCaratRange}
          selectedColors={selectedColors}
          selectedClarities={selectedClarities}
          selectedShape={selectedShape}
          selectedCaratRange={selectedCaratRange}
          // setQueryParams={setQueryParams}
          caratRanges={caratRanges}
          selectionMode={selectionMode}
          colorGroupsApi={colorGroupsApi}
          clarityGroupsApi={clarityGroupsApi}
          shapes={shapes}
          data={data}
          selectedColorGroup={selectedColorGroup}
          selectedClarityGroup={selectedClarityGroup}
          setSalesFilterKey={setSalesFilterKey}
          setInventoryFilterKey={setInventoryFilterKey}
          handleShapeChange={handleShapeChange}
          handleCaratChange={handleCaratChange}
          handleColorChange={handleColorChange}
          handleClarityChange={handleClarityChange}
          handleSelectionChange={handleSelectionChange}
          handleSearchClick={handleSearchClick}
          salesFilter={salesFilter}
          inventoryFilter={inventoryFilter}    
          inventoryFilterKey={inventoryFilterKey}
          salesFilterKey={salesFilterKey}              
       />
      )}
    </div>
    
    </>
  );
};


ColorClarityGrid.propTypes = {
  shape: PropTypes.string.isRequired,
  caratRange: PropTypes.string.isRequired,
  onCellClick: PropTypes.func.isRequired,
};

export default ColorClarityGrid;





